import { Button } from '@mui/material';
import { DocumentStatus, DocumentType, SalesOrderStatus, getOrderTypeTranslationId } from '@synergy/models';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { Fragment, ReactElement, useEffect, useState } from 'react';
import {
    PaloAmount,
    PaloColumn,
    PaloListView,
    PaloRowAction,
    PaloStatusPill,
    usePaloListSort,
} from '@synergy/components-palo';
import { UrlFilter } from '@synergy/components';
import { getDocumentStatusTranslationId } from '@synergy/utils';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import * as SalesOrderActions from '../sales-order-actions';
import { ContactPresenter } from '../../../common/components/contact-presenter/contact-presenter';
import { ConverterReceiptModal } from '../../convert-receipt/converter-receipt-modal';
import { PaloContactName } from '../../../common/components/palo/palo-contact-card';
import { PaloNamedFilterChips } from '../../../common/components/palo/palo-named-filter-chips';
import { PaloSalesOrderOverviewFilter } from './palo-sales-order-overview-filter';
import { SalesOrder } from '../models/salesOrder';
import { SalesOrderTransition } from '../../convert-receipt/models/sales-order/salesOrdersTransition';
import { Transition } from '../../convert-receipt/models/type-transition';
import { TransitionTarget } from '../../convert-receipt/models/internal/transition-target';
import { fetchTransitions, resetTransitions } from '../../../common/actions/transitions-activity-actions';
import { lockedPerson } from '../../../common/utils/locked-partner-notice';
import {
    transitionSalesOrderIntoInquiries,
    transitionSalesOrderIntoPurchaseOrders,
} from '../../convert-receipt/transition-actions';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { usePaloColumnFilters } from '../../../common/components/palo/use-palo-column-filters';
import { usePaloListViews } from '../../../common/components/palo/use-palo-list-views';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

/** The amount column names the currency in its head, as the order list does. */
const columnsIn = (currency: string): PaloColumn<SalesOrder>[] => [
    {
        key: 'number',
        type: 'documentNumber',
        sortKey: 'number',
        head: <FormattedMessage id="app.table.column.numberAbbreviation" />,
        cell: order => order.number,
    },
    {
        key: 'status',
        type: 'status',
        sortKey: 'status',
        head: <FormattedMessage id="app.table.column.status" />,
        // The status is chosen in the navigation on the left and nowhere else: the
        // same filter in two places wrote two different keys into the address and
        // answered each other (LST-S9, GF 13.10.2026). The active one stands as a
        // chip over the list, where it can be taken off again.
        cell: order => (
            <PaloStatusPill
                status={order.status}
                label={<FormattedMessage id={getDocumentStatusTranslationId(order.status)} />}
            />
        ),
    },
    {
        key: 'type',
        sortKey: 'type',
        head: <FormattedMessage id="app.table.column.type" />,
        cell: order => <FormattedMessage id={getOrderTypeTranslationId(order.type)} />,
    },
    {
        key: 'customer',
        type: 'name',
        sortKey: 'customerAccount',
        head: <FormattedMessage id="app.table.column.customer" />,
        cell: order => <PaloContactName company={order.customerAccount} kind="customer" />,
    },
    {
        // Right behind the customer: the two are read together (#2260).
        key: 'customerContact',
        type: 'name',
        sortKey: 'customerContact',
        head: <FormattedMessage id="app.table.column.customerContact" />,
        cell: order => (
            <ContactPresenter
                person={order.customerContact}
                fullName={order.customerContact?.fullName}
                isDeleted={order.customerContact?.isDeleted}
                isLocked={order.customerContact?.isLocked}
            />
        ),
    },
    {
        // A sales order has no supplier of its own, so the column names every
        // supplier on its positions, once each (#2193), each with its card (#2260).
        key: 'supplier',
        type: 'name',
        wrap: true,
        head: <FormattedMessage id="app.table.column.supplier" />,
        cell: order =>
            order.suppliers?.map((supplier, index) => (
                <Fragment key={supplier.id ?? supplier.name}>
                    {0 < index && ', '}
                    <PaloContactName company={supplier} kind="supplier" />
                </Fragment>
            )),
    },
    {
        key: 'total',
        type: 'amount',
        currency,
        head: <FormattedMessage id="app.table.column.salesOrderTotal" />,
        // A single inquiry has no prices yet; "–" says so, 0,00 reads as a
        // value (R-174, like O-15).
        cell: order => (order.totalNetPrice ? <PaloAmount value={order.totalNetPrice} /> : '–'),
    },
    {
        key: 'createdAt',
        type: 'date',
        sortKey: 'createdAt',
        head: <FormattedMessage id="app.table.column.createdAt" />,
        cell: order => <FormattedDate value={order.createdAt} year="numeric" month="short" day="2-digit" />,
    },
];

export const PaloSalesOrderOverview = (): ReactElement => {
    const { hiddenColumnKeys, shownColumnKeys, toggleColumn, moveColumn, orderColumns, savedFiltersMenu } =
        usePaloListViews('sales-orders');
    const columnFilter = usePaloColumnFilters('sales-orders');
    const dispatch = useThunkDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { sort, params } = usePaloListSort('sales-orders');
    const intl = useIntl();
    const { flat: paginated, requesting, entity: salesOrder } = useAppSelector(state => state.salesOrders);
    const currency = useAppSelector(state => state.settings.currency);

    const page = Number(searchParams.get('page') ?? 1); // 1-based
    const limit = Number(searchParams.get('limit') ?? 50);

    useEffect(() => {
        dispatch(SalesOrderActions.fetchSalesOrders(params));
    }, [dispatch, params]);

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const openDetail = (order: SalesOrder): void => navigate(location.pathname + '/' + order.id);

    const headerActions = (
        <Button variant="contained" color="primary" onClick={() => navigate(location.pathname + '/new')}>
            <FormattedMessage id="app.salesOrders.new" />
        </Button>
    );

    const [isConverterVisible, setConverterVisible] = useState(false);

    const handleConvertOpen = (id: number): void => {
        dispatch(SalesOrderActions.resetSalesOrderState());
        dispatch(resetTransitions());
        dispatch(fetchTransitions(DocumentType.SalesOrder, id));
        dispatch(SalesOrderActions.fetchSalesOrder(id));
        setConverterVisible(true);
    };

    const handleConvertClose = (): void => {
        dispatch(SalesOrderActions.requestEnd());
        dispatch(SalesOrderActions.resetSalesOrderState());
        setConverterVisible(false);
    };

    // The dialog opens what was made; jumping to a list here took the reader
    // away from it (#2316).
    const handleTransition = (transitionObject: Transition, target: TransitionTarget) =>
        TransitionTarget.PurchaseOrder === target
            ? dispatch(transitionSalesOrderIntoPurchaseOrders(transitionObject as SalesOrderTransition))
            : dispatch(transitionSalesOrderIntoInquiries(transitionObject as SalesOrderTransition));

    // A cart waiting for the member's approval, or declined by it, is the
    // member's to decide: POOLALPIN reads it, but neither converts nor deletes
    // it, as in the opened cart (R-165, R-171).
    const isMembersDecision = (order: SalesOrder): boolean => {
        // The model types the status as a document status; carts carry their own.
        const status = order.status as DocumentStatus | SalesOrderStatus;

        return SalesOrderStatus.CartPending === status || SalesOrderStatus.Declined === status;
    };

    const rowActions = (order: SalesOrder): PaloRowAction[] => [
        { label: 'Öffnen', onClick: () => openDetail(order) },
        ...(isMembersDecision(order) ? [] : [{ label: 'Umwandeln', onClick: () => handleConvertOpen(order.id) }]),
        {
            label: 'Duplizieren',
            onClick: () =>
                dispatch(
                    SalesOrderActions.duplicateSalesOrder(order.id, [
                        order.customerAccount,
                        lockedPerson(order.customerContact),
                    ])
                ),
        },
        ...(isMembersDecision(order)
            ? []
            : [{ label: 'Löschen', onClick: () => dispatch(SalesOrderActions.deleteSalesOrder(order.id)) }]),
    ];

    const handleExport = (): void => {
        const filename = `${intl.formatMessage({ id: 'app.export.salesOrders' })}.csv`;
        dispatch(SalesOrderActions.exportSalesOrders(filename, searchParams));
    };

    return (
        <UrlFilter useLegacyFilter={true}>
            <PaloListView<SalesOrder>
                title={<FormattedMessage id="app.navigation.salesOrders" />}
                pageTitle={intl.formatMessage({ id: 'app.navigation.salesOrders' })}
                onExport={handleExport}
                filters={
                    <>
                        <PaloSalesOrderOverviewFilter />
                        {savedFiltersMenu}
                    </>
                }
                filterChips={<PaloNamedFilterChips listKey="sales-orders" />}
                sort={sort}
                onSortChange={(key, order) => updateParams({ sort: key, order, page: '1' })}
                searchValue={searchParams.get('search') ?? ''}
                searchPlaceholder="Kunde, Referenz, Artikel …"
                onSearchChange={value => {
                    const next = new URLSearchParams(searchParams);
                    if (value) {
                        next.set('search', value);
                    } else {
                        next.delete('search');
                    }
                    next.set('page', '1');
                    setSearchParams(next, { replace: true });
                }}
                headerActions={headerActions}
                columns={orderColumns(columnsIn(currency))}
                columnFilter={columnFilter}
                onMoveColumn={moveColumn}
                hiddenColumnKeys={hiddenColumnKeys}
                shownColumnKeys={shownColumnKeys}
                onToggleColumn={toggleColumn}
                items={paginated.items}
                getRowId={order => order.id}
                requesting={requesting}
                emptyLabel="Keine Aufträge."
                onRowClick={openDetail}
                rowActions={rowActions}
                pagination={{
                    count: paginated.total,
                    page,
                    limit,
                    onPageChange: newPage => updateParams({ page: String(newPage) }),
                    onLimitChange: newLimit => updateParams({ limit: String(newLimit), page: '1' }),
                }}
            >
                {salesOrder && (
                    <ConverterReceiptModal
                        isVisible={isConverterVisible}
                        source={salesOrder}
                        onClose={handleConvertClose}
                        onConvert={handleTransition}
                    />
                )}
            </PaloListView>
        </UrlFilter>
    );
};
